import { memo, useCallback, useEffect, useMemo, useState } from "react"

import { Table } from "react-bootstrap"

import SelectPoolForRunGridHeader from "./SelectPoolForRunGridHeader"
import SelectPoolForRunGridRow from "./SelectPoolForRunGridRow"
import { Filter, Sort } from "../../../api/GridRequest"
import { Pool } from "../../../api/Randoms/RandomsModel/PoolData"
import { ColumnFilters, DEFAULT_COLUMNS } from "../../../api/Randoms/SelectPoolForRunGridColumns"

interface SelectPoolForRunGridProps {
  pools: Pool[]
  selectedPools: Pool[]
  isConsortium: boolean
  cancel: boolean
  readonly?: boolean
  onSelectPool: (pools: Pool[], checked: boolean) => void
}

interface Filters extends Partial<ColumnFilters> {
  PoolName: string[]
  SelectionInterval: string[]
}

function SelectPoolForRunGrid({
  pools,
  selectedPools,
  isConsortium,
  cancel,
  readonly = false,
  onSelectPool,
}: SelectPoolForRunGridProps) {
  const [filtersData, setFiltersData] = useState<Filters>({} as Filters)
  const [filters, setFilters] = useState<Filter[]>([])
  const [sort, setSort] = useState<Sort>({} as Sort)

  useEffect(() => {
    setFiltersData({
      PoolName: pools.map((pool) => pool.PoolName).filter((v, i, self) => self.indexOf(v) === i),
      SelectionInterval: pools.map((pool) => pool.SelectionInterval).filter((v, i, self) => self.indexOf(v) === i),
    })
  }, [pools])

  const visibleColumns = useMemo(() => {
    const skipColumns = cancel ? ["SelectionInterval", "LastSelection", "DrugSampleSize", "AlcoholSampleSize"] : []

    const filteredColumns = DEFAULT_COLUMNS.map((column) =>
      skipColumns.includes(column.name) ? { ...column, visible: false } : column
    )

    return filteredColumns.filter((column) => column.visible)
  }, [cancel])

  const filteredPools = useMemo(
    () =>
      pools
        .filter((pool) =>
          filters.every((filter) => {
            const value = pool[filter.column as keyof Pool] as string
            return filter.values.length === 0 || filter.values.includes(value)
          })
        )
        .sort((a, b) => {
          if (!sort.column || !sort.order) return 0

          const aValue = a[sort.column as keyof Pool] as string
          const bValue = b[sort.column as keyof Pool] as string

          return sort.order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
        }),
    [filters, pools, sort.column, sort.order]
  )

  const allPoolsChecked = useMemo(() => {
    return pools.length > 0 && pools.every((pool) => selectedPools.includes(pool))
  }, [pools, selectedPools])

  const updateFilter = useCallback(
    (column: string, items: string[]) => {
      setFilters((prev) => {
        const existingIndex = prev.findIndex((f) => f.column === column)

        if (existingIndex >= 0) {
          const updated = [...prev]
          updated[existingIndex] = { ...prev[existingIndex], values: items }
          return updated
        }
        return [...prev, { column, values: items }]
      })
      // Reset all selected pools when filtering
      onSelectPool(pools, false)
    },
    [onSelectPool, pools]
  )

  const handleAllPoolsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSelectPool(pools, event.target.checked)
    },
    [pools, onSelectPool]
  )

  const handleSelectPoolChange = useCallback(
    (pool: Pool, checked: boolean) => {
      onSelectPool([pool], checked)
    },
    [onSelectPool]
  )

  const handleSortGridChange = useCallback((order?: string, column?: string) => {
    setSort({ order: order || "", column: column || "" })
  }, [])

  return (
    <Table responsive bordered className="dark m-0">
      <thead>
        <SelectPoolForRunGridHeader
          columns={visibleColumns}
          filtersData={filtersData}
          filters={filters}
          allPoolsChecked={allPoolsChecked}
          sort={sort}
          isConsortium={isConsortium}
          readonly={readonly}
          onAllPoolsChange={handleAllPoolsChange}
          onSortChange={handleSortGridChange}
          onFilterChange={updateFilter}
        />
      </thead>
      <tbody>
        {filteredPools.map((pool) => (
          <SelectPoolForRunGridRow
            pool={pool}
            visibleColumns={visibleColumns}
            checked={selectedPools.includes(pool)}
            readonly={readonly}
            onSelectPool={handleSelectPoolChange}
            key={pool.PoolID}
          />
        ))}
      </tbody>
    </Table>
  )
}

export default memo(SelectPoolForRunGrid)
